import React, { useRef } from "react";
import ReactDOM from "react-dom/client";
import { motion, useInView } from "motion/react";

/**
 * MotionScrollText - Scroll-driven text reveal powered by Motion (motion.dev)
 * Splits text into words/tokens and reveals them smoothly as they enter the viewport.
 */
export function MotionScrollText({
	children,
	text,
	as = "h2",
	className = "",
	delay = 0,
	stagger = 0.035,
	once = true,
}) {
	const ref = useRef(null);
	const isInView = useInView(ref, {
		once,
		margin: "-60px 0px -40px 0px",
	});

	const content = text || (typeof children === "string" ? children : "");
	const words = content.trim().split(/\s+/).filter(Boolean);

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: stagger,
				delayChildren: delay,
			},
		},
	};

	const wordVariants = {
		hidden: {
			opacity: 0,
			y: 24,
			filter: "blur(6px)",
		},
		visible: {
			opacity: 1,
			y: 0,
			filter: "blur(0px)",
			transition: {
				duration: 0.55,
				ease: [0.16, 1, 0.3, 1],
			},
		},
	};

	const MotionComponent = motion[as] || motion.div;

	// If complex children passed instead of plain string, animate as a block
	if (!words.length && children) {
		return (
			<MotionComponent
				ref={ref}
				className={`solufy-motion-text ${className}`}
				initial={{ opacity: 0, y: 28, filter: "blur(4px)" }}
				animate={isInView ? { opacity: 1, y: 0, filter: "blur(0px)" } : { opacity: 0, y: 28, filter: "blur(4px)" }}
				transition={{ duration: 0.65, delay, ease: [0.16, 1, 0.3, 1] }}
			>
				{children}
			</MotionComponent>
		);
	}

	return (
		<MotionComponent
			ref={ref}
			className={`solufy-motion-text ${className}`}
			variants={containerVariants}
			initial="hidden"
			animate={isInView ? "visible" : "hidden"}
		>
			{words.map((word, idx) => (
				<motion.span
					key={`${word}-${idx}`}
					variants={wordVariants}
					style={{
						display: "inline-block",
						marginRight: "0.28em",
						willChange: "transform, opacity, filter",
					}}
				>
					{word}
				</motion.span>
			))}
		</MotionComponent>
	);
}

/**
 * MotionCard - Motion scroll reveal for cards, badges, and grids
 */
export function MotionCard({
	children,
	className = "",
	delay = 0,
	y = 30,
}) {
	return (
		<motion.div
			className={className}
			initial={{ opacity: 0, y, filter: "blur(3px)" }}
			whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
			viewport={{ once: true, margin: "-50px" }}
			transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
		>
			{children}
		</motion.div>
	);
}

/**
 * mountMotionScroll - Mounts Motion on DOM elements marked with [data-motion-scroll] or [data-motion-text]
 */
export function mountMotionScroll(root = document) {
	// Check for reduced motion preference
	if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
		return;
	}

	// 1. Mount React Motion on explicit text elements
	const motionElements = root.querySelectorAll("[data-motion-text]");
	motionElements.forEach((el) => {
		if (el.dataset.motionMounted) return;
		el.dataset.motionMounted = "true";

		const rawText = el.textContent || "";
		const asTag = el.dataset.motionAs || el.tagName.toLowerCase();
		const delay = parseFloat(el.dataset.motionDelay || "0");
		const stagger = parseFloat(el.dataset.motionStagger || "0.035");
		const extraClass = el.className;

		const wrapper = document.createElement("div");
		wrapper.className = "solufy-motion-host";
		el.parentNode.replaceChild(wrapper, el);

		const reactRoot = ReactDOM.createRoot(wrapper);
		reactRoot.render(
			<MotionScrollText
				text={rawText}
				as={asTag}
				className={extraClass}
				delay={delay}
				stagger={stagger}
			/>
		);
	});

	// 2. High performance Motion-style scroll observer for [data-reveal]
	// Using IntersectionObserver with Framer/Motion easing curve
	const revealElements = root.querySelectorAll("[data-reveal]:not([data-reveal-active])");
	if (revealElements.length && "IntersectionObserver" in window) {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target.classList.add("is-revealed");
						entry.target.dataset.revealActive = "true";
						observer.unobserve(entry.target);
					}
				});
			},
			{
				rootMargin: "0px 0px -60px 0px",
				threshold: 0.12,
			}
		);

		revealElements.forEach((el) => observer.observe(el));
	}
}
